import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useExplorer } from '@/hooks/useExplorer';
import {
  CodeSample,
  PostmanUtil,
} from '@/services/postmanCollection/postman_util';
import { SampleUseCase } from '@components/ui/CodeSample/SampleUseCase';
import { convertCodeSampleToEndpointAttributes } from '@/services/postmanCollection/Convertor';
import { clearForm } from '@/services/ClearForm';
import { USECASE_IN_SEARCH_PARAM } from '@/constant/Constants';
import { isFieldFilled } from '@/services/util';
import { useSearchParams } from 'next/navigation';

export interface CodeSampleContainerProps {
  selectedCodeSample: string;
  setSelectedCodeSample: (value: string) => void;
  rawCodeSample: string;
  codeSample?: CodeSample;
  setCodeSample: Dispatch<SetStateAction<CodeSample | undefined>>;
  setIsEmptyAttributesVisible: Dispatch<SetStateAction<boolean>>;
}

export const CodeSampleContainer: React.FC<CodeSampleContainerProps> = ({
  selectedCodeSample,
  setSelectedCodeSample,
  rawCodeSample,
  codeSample,
  setCodeSample,
  setIsEmptyAttributesVisible,
}) => {
  const {
    endpoint,
    endpointAttributes,
    setEndpointAttributes,
    setResponsePayload,
    setVisibleFieldsOverride,
  } = useExplorer();
  const [codeSamples, setCodeSamples] = useState<CodeSample[]>([]);
  const searchParams = useSearchParams();

  const handleClearSelection = () => {
    setSelectedCodeSample('');
    setCodeSample(undefined);
    setResponsePayload();
    if (endpoint && endpointAttributes) {
      const updatedEndpointAttributes = clearForm(
        endpoint.method,
        endpointAttributes
      );
      if (updatedEndpointAttributes) {
        setEndpointAttributes({ ...updatedEndpointAttributes });
      }
    }
  };

  const base64EncodedUseCase = searchParams.get(USECASE_IN_SEARCH_PARAM);

  useEffect(() => {
    handleClearSelection();
    if (base64EncodedUseCase && codeSamples) {
      const selectedUseCase = codeSamples.find((codeSample) => {
        return btoa(JSON.stringify(codeSample.params)) == base64EncodedUseCase;
      });
      setCodeSample(selectedUseCase);
      setSelectedCodeSample(selectedUseCase?.desc || '');
    }
  }, [codeSamples, base64EncodedUseCase]);

  useEffect(() => {
    const loadSample = async () => {
      try {
        const codeSamples = new PostmanUtil(JSON.parse(rawCodeSample)).build();
        setCodeSamples(codeSamples);
      } catch (error) {
        return Promise.reject(error);
      }
    };
    loadSample();
  }, [rawCodeSample]);

  useEffect(() => {
    if (endpoint && endpointAttributes) {
      const updatedEndpointAttributes = convertCodeSampleToEndpointAttributes(
        endpoint.method,
        endpoint.url,
        endpointAttributes,
        codeSample
      );
      if (updatedEndpointAttributes) {
        setEndpointAttributes({ ...updatedEndpointAttributes });
        // localStorage.setItem(
        //   `input_cache`,
        //   atob(base64EncodedFormData(updatedEndpointAttributes, endpoint))
        // );
        setIsEmptyAttributesVisible(false);
        const visibleFields: Array<string> = [];
        [
          updatedEndpointAttributes?.params,
          updatedEndpointAttributes?.queryParams,
        ].map((endpointAttribute) => {
          endpointAttribute?.map((field) => {
            if (isFieldFilled(field)) {
              visibleFields.push(field.key);
            }
          });
        });
        setVisibleFieldsOverride([...visibleFields]);
        setEndpointAttributes({ ...updatedEndpointAttributes });
      }
    }
  }, [codeSample]);

  return (
    <SampleUseCase
      samples={codeSamples}
      setCodeSample={setCodeSample}
      value={selectedCodeSample}
      setSelectedValue={setSelectedCodeSample}
      clearSelection={handleClearSelection}
    />
  );
};

export default CodeSampleContainer;
